import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, LayoutList, Table2, Package } from 'lucide-react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import ApprovalRequestsList from '../components/ApprovalRequestsList';
import ApprovalGroupedTable from '../components/ApprovalGroupedTable';
import ApprovalDetailsDrawer from '../components/ApprovalDetailsDrawer';
import {
  ApprovalRequest,
  getPendingApprovals,
  getMyApprovalRequests,
  approveRequest,
  rejectRequest,
  deleteApprovalRequest,
  getApprovalStats,
  ApprovalStats,
} from '../../../services/approvalService';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
const Approvals: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState<ApprovalRequest[]>([]);
  const [myRequests, setMyRequests] = useState<ApprovalRequest[]>([]);
  const [stats, setStats] = useState<ApprovalStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null);
  const [modalAction, setModalAction] = useState<'approve' | 'reject' | 'view'>('view');
  const [modalOpen, setModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [pendingPage, setPendingPage] = useState(0);
  const [pendingRowsPerPage, setPendingRowsPerPage] = useState(10);
  const [pendingTotal, setPendingTotal] = useState(0);

  const [myRequestsPage, setMyRequestsPage] = useState(0);
  const [myRequestsRowsPerPage, setMyRequestsRowsPerPage] = useState(10);
  const [myRequestsTotal, setMyRequestsTotal] = useState(0);

  const [myRequestsStatusFilter, setMyRequestsStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');

  const navigate = useNavigate();
  const { user } = useAuth();
  const isApprover = user?.role?.roleType === 'approver' || user?.role?.roleType === 'both';
  const isRequester = user?.role?.roleType === 'requester';

  React.useEffect(() => {
    if (activeTab === 0) {
      if (isApprover) {
        loadPendingApprovals();
      } else {
        loadMyRequests();
      }
    } else if (activeTab === 1 && isApprover) {
      loadMyRequests();
    }
    loadStats();
  }, [activeTab, pendingPage, pendingRowsPerPage, myRequestsPage, myRequestsRowsPerPage, myRequestsStatusFilter]);

  const loadPendingApprovals = async () => {
    if (!isApprover) return;
    setLoading(true);
    try {
      const response = await getPendingApprovals({
        page: pendingPage + 1,
        limit: pendingRowsPerPage,
      });
      const nonProjectApprovals = (response.data || []).filter(
        (req: ApprovalRequest) => req.resourceType !== 'project'
      );
      setPendingApprovals(nonProjectApprovals);
      setPendingTotal(nonProjectApprovals.length);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e.response?.data?.message || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const loadMyRequests = async () => {
    setLoading(true);
    try {
      const response = await getMyApprovalRequests({
        page: myRequestsPage + 1,
        limit: myRequestsRowsPerPage,
        ...(myRequestsStatusFilter !== 'all' && { status: myRequestsStatusFilter }),
      });
      const nonProjectRequests = (response.data || []).filter(
        (req: ApprovalRequest) => req.resourceType !== 'project'
      );
      setMyRequests(nonProjectRequests);
      setMyRequestsTotal(nonProjectRequests.length);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e.response?.data?.message || 'Failed to load my requests');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await getApprovalStats();
      setStats(response.data);
    } catch {
      // Stats are non-critical
    }
  };

  const handleTabChange = (newTab: number) => setActiveTab(newTab);

  const handleApprove = (request: ApprovalRequest) => {
    setSelectedRequest(request);
    setModalAction('approve');
    setModalOpen(true);
  };

  const handleReject = (request: ApprovalRequest) => {
    setSelectedRequest(request);
    setModalAction('reject');
    setModalOpen(true);
  };

  const handleView = (request: ApprovalRequest) => {
    if (request.resourceType === 'project') {
      toast.info('Project approvals have moved to the dedicated Project Approvals page');
      return;
    }
    setSelectedRequest(request);
    setModalAction('view');
    setModalOpen(true);
  };

  const handleDelete = async (request: ApprovalRequest) => {
    if (!window.confirm('Are you sure you want to delete this approval request?')) return;
    const requestId = request._id || (request as { id?: string }).id;
    if (!requestId) {
      toast.error('Invalid approval request ID');
      return;
    }
    try {
      await deleteApprovalRequest(requestId);
      toast.success('Approval request deleted successfully');
      loadMyRequests();
      loadStats();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e.response?.data?.message || 'Failed to delete request');
    }
  };

  const handleConfirmAction = async (comments: string) => {
    if (!selectedRequest) return;
    const requestId = selectedRequest._id || (selectedRequest as { id?: string }).id;
    if (!requestId) {
      toast.error('Invalid approval request ID');
      return;
    }
    setActionLoading(true);
    try {
      if (modalAction === 'approve') {
        await approveRequest(requestId, comments);
        toast.success('Request approved successfully');
      } else if (modalAction === 'reject') {
        if (!comments?.trim()) throw new Error('Rejection reason is required');
        await rejectRequest(requestId, comments);
        toast.success('Request rejected successfully');
      }
      if (activeTab === 0) loadPendingApprovals();
      else loadMyRequests();
      loadStats();
      setModalOpen(false);
    } catch (err: unknown) {
      const e = err as Error & { response?: { data?: { message?: string } } };
      throw new Error(e.response?.data?.message || e.message || 'Failed to process request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefresh = () => {
    if (activeTab === 0 && isApprover) loadPendingApprovals();
    else if (activeTab === 1 || !isApprover) loadMyRequests();
    loadStats();
  };

  const totalAll = stats ? stats.totalPending + stats.totalApproved + stats.totalRejected : 0;

  return (
    <div className="w-full min-h-screen px-4 sm:px-6 lg:px-8 py-6 font-[family-name:var(--font-outfit)]">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-[var(--color-black)] tracking-tight">
            Approval Requests
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Review and manage approval workflow
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'table'
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Table View"
            >
              <Table2 className="w-3.5 h-3.5" />
              Table
            </button>
            <button
              type="button"
              onClick={() => setViewMode('cards')}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'cards'
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Card View"
            >
              <LayoutList className="w-3.5 h-3.5" />
              Cards
            </button>
          </div>
          <Button
            icon="pi pi-refresh"
            label="Refresh"
            className="p-button-outlined p-button-sm"
            loading={loading}
            onClick={handleRefresh}
          />
        </div>
      </div>

      {/* Stats — compact Prime-style cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
          <Card className="!shadow-sm !border !border-gray-200 !rounded-xl overflow-hidden">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-amber-700 uppercase tracking-wider">Pending</p>
                <p className="text-xl font-semibold text-gray-900 tabular-nums">{stats.totalPending}</p>
                {stats.totalPending > 0 && (
                  <p className="text-[10px] text-gray-500 mt-0.5">Awaiting your action</p>
                )}
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </Card>
          <Card className="!shadow-sm !border !border-gray-200 !rounded-xl overflow-hidden">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-emerald-700 uppercase tracking-wider">Approved</p>
                <p className="text-xl font-semibold text-gray-900 tabular-nums">{stats.totalApproved}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <span className="text-emerald-600 text-lg leading-none">✓</span>
              </div>
            </div>
          </Card>
          <Card className="!shadow-sm !border !border-gray-200 !rounded-xl overflow-hidden">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-red-700 uppercase tracking-wider">Rejected</p>
                <p className="text-xl font-semibold text-gray-900 tabular-nums">{stats.totalRejected}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <span className="text-red-600 text-lg leading-none">✕</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Card className="!shadow-sm !border !border-gray-200 !rounded-xl overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex gap-0 -mb-px" aria-label="Tabs">
            {isApprover && (
              <button
                type="button"
                onClick={() => handleTabChange(0)}
                className={`whitespace-nowrap py-3.5 px-4 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === 0
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending ({stats?.totalPending ?? 0})
              </button>
            )}
            <button
              type="button"
              onClick={() => handleTabChange(isApprover ? 1 : 0)}
              className={`whitespace-nowrap py-3.5 px-4 border-b-2 text-sm font-medium transition-colors ${
                activeTab === (isApprover ? 1 : 0)
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Requests
            </button>
            <button
              type="button"
              onClick={() => navigate('/approvals/projects')}
              className="whitespace-nowrap py-3.5 px-4 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 flex items-center gap-1.5"
            >
              <Package className="w-4 h-4" />
              Project Approvals
            </button>
          </nav>
        </div>

        <div className="p-4 sm:p-5">
          {isApprover && activeTab === 0 && (
            <>
              {viewMode === 'table' ? (
                <ApprovalGroupedTable
                  approvalRequests={pendingApprovals}
                  loading={loading}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onView={handleView}
                  isApprover={true}
                  groupByLocation={true}
                />
              ) : (
                <ApprovalRequestsList
                  approvalRequests={pendingApprovals}
                  loading={loading}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onView={handleView}
                  isApprover={true}
                  groupByUser={false}
                  groupByLocation={true}
                  page={pendingPage}
                  rowsPerPage={pendingRowsPerPage}
                  totalCount={pendingTotal}
                  onPageChange={setPendingPage}
                  onRowsPerPageChange={setPendingRowsPerPage}
                />
              )}
            </>
          )}

          {activeTab === (isApprover ? 1 : 0) && (
            <>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {[
                  { key: 'all' as const, label: 'All', count: totalAll },
                  { key: 'pending' as const, label: 'Pending', count: stats?.totalPending ?? 0 },
                  { key: 'approved' as const, label: 'Approved', count: stats?.totalApproved ?? 0 },
                  { key: 'rejected' as const, label: 'Rejected', count: stats?.totalRejected ?? 0 },
                ].map(({ key, label, count }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setMyRequestsStatusFilter(key);
                      setMyRequestsPage(0);
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      myRequestsStatusFilter === key
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                    <span className="tabular-nums opacity-80">({count})</span>
                  </button>
                ))}
              </div>

              {viewMode === 'table' ? (
                <ApprovalGroupedTable
                  approvalRequests={myRequests}
                  loading={loading}
                  onView={handleView}
                  onDelete={handleDelete}
                  isRequester={true}
                />
              ) : (
                <ApprovalRequestsList
                  approvalRequests={myRequests}
                  loading={loading}
                  onView={handleView}
                  onDelete={handleDelete}
                  isRequester={true}
                  groupByUser={false}
                  page={myRequestsPage}
                  rowsPerPage={myRequestsRowsPerPage}
                  totalCount={myRequestsTotal}
                  onPageChange={setMyRequestsPage}
                  onRowsPerPageChange={setMyRequestsRowsPerPage}
                />
              )}
            </>
          )}
        </div>
      </Card>

      <ApprovalDetailsDrawer
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        approvalRequest={selectedRequest}
        action={modalAction}
        onConfirm={handleConfirmAction}
        loading={actionLoading}
      />
    </div>
  );
};

export default Approvals;
